import type { SalesOrder } from '@/lib/api/sales-order-api'
import { orderRemaining } from '@/lib/order-money'

/** How the customer settled when the driver handed the goods over. */
export type SettlementMethod = 'cash' | 'bank_transfer' | 'credit'

/** Settlement part of the confirm-delivery payload. */
export interface DeliverySettlement {
    method: SettlementMethod
    /** Partial cash collection; omitted = the full remaining debt. */
    cash_amount?: number
    /** With bank_transfer: paid into the driver's own account (M-133). */
    transfer_to_driver: boolean
    collect_shipping_fee: boolean
}

/** Where a bank transfer landed: the HTX account or the driver's own. */
export type TransferTarget = 'htx' | 'driver'

/** Order fields the settlement decision reads; trip rows carry all of them. */
export type SettlementOrder = Pick<SalesOrder, 'total_amount' | 'paid_amount'> &
    Partial<
        Pick<
            SalesOrder,
            | 'returned_amount'
            | 'return_fee_amount'
            | 'collect_cod'
            | 'shipping_fee'
            | 'shipping_fee_payer'
        >
    >

/**
 * The driver must say how the customer paid when the order was created for
 * cash-on-delivery and still has money due. Orders not marked COD behave like
 * "không thu" on the server (cash is refused there), so they keep the one-tap
 * confirm, as do COD orders with nothing left to collect.
 */
export function needsSettlementChoice(order: SettlementOrder): boolean {
    return order.collect_cod === true && Math.round(orderRemaining(order)) > 0
}

/** The customer owes the carrier fee, so the driver may collect it in cash. */
export function isShippingFeeCollectible(
    order:
        | Pick<SettlementOrder, 'shipping_fee' | 'shipping_fee_payer'>
        | undefined,
): boolean {
    return Boolean(
        order?.shipping_fee &&
        order.shipping_fee > 0 &&
        order.shipping_fee_payer === 'customer',
    )
}

/**
 * Validated chooser values → confirm-delivery payload. The amount goes out only
 * when the driver holds the money (cash, or a transfer into the driver's own
 * account) and differs from `due`: the full amount stays implicit so the server
 * settles exactly what is still owed.
 */
export function toDeliverySettlement(
    values: {
        method: SettlementMethod
        cash_amount: string
        transfer_target: TransferTarget | ''
        collect_fee: boolean
    },
    feeCollectible: boolean,
    due = 0,
): DeliverySettlement {
    const toDriver =
        values.method === 'bank_transfer' && values.transfer_target === 'driver'
    const holds = values.method === 'cash' || toDriver
    const raw = values.cash_amount.trim()
    const amount = holds && raw !== '' ? Number(raw) : undefined
    return {
        method: values.method,
        cash_amount:
            amount === undefined || amount === due ? undefined : amount,
        transfer_to_driver: toDriver,
        collect_shipping_fee: feeCollectible && values.collect_fee,
    }
}
